import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import { Logo } from '../components/Logo.js'
import { Notice, Spinner } from '../components/ui.js'
import { t } from '../i18n/index.js'

interface InviteInfo {
  name: string
  tenantName: string
  attemptsLeft: number
}

export function InvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InviteInfo>(`/api/auth/invite/${token}`),
    retry: false,
  })

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/api/auth/invite/redeem', { method: 'POST', body: { token, code } })
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      navigate('/app', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('error.generic'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) return <div className="center-page"><Spinner /></div>

  if (isError || !data) {
    return (
      <div className="center-page">
        <div className="box">
          <Notice kind="error">{t('invite.invalid')}</Notice>
        </div>
      </div>
    )
  }

  return (
    <div className="center-page">
      <div className="box">
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <Logo size={64} />
          <h1 style={{ fontSize: 24, marginTop: 12 }}>{t('invite.hello', { name: data.name })}</h1>
          <p style={{ fontSize: 17, color: 'var(--ink-500)' }}>{data.tenantName}</p>
        </div>

        {error && <Notice kind="error">{error}</Notice>}

        <form onSubmit={submit}>
          <p style={{ fontSize: 17, textAlign: 'center' }}>{t('invite.enter_code')}</p>
          <input
            className="wk-code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            autoFocus
            aria-label={t('invite.code')}
          />
          <button type="submit" className="wk-btn" style={{ marginTop: 18 }} disabled={code.length !== 6 || busy}>
            {busy ? t('invite.checking') : t('invite.submit')}
          </button>
        </form>

        <p className="muted" style={{ fontSize: 15, textAlign: 'center', marginTop: 22 }}>
          {t('invite.installed_hint')}
        </p>
      </div>
    </div>
  )
}
